import React, { useCallback, useContext, useMemo, useRef } from 'react'
import { connect } from 'react-redux'
import styled from 'styled-components'
import OfferGallery from '../OfferGallery'
import Pane from 'components/Common/Pane'
import PackageOptions from './FlashTourPackageOptions'
import { scheduleIsCurrent } from 'lib/offer/scheduleStatusUtils'
import PaneBody from 'components/Common/Pane/PaneBody'
import LayoutContainer from 'components/Common/LayoutContainer'
import ScrollAnchor from 'components/Common/ScrollAnchor'
import InlineGroup from 'components/Common/InlineGroup'
import ShareModal from 'components/Common/Modals/ShareModal'
import OfferIntro from './FlashTourIntro'
import OfferBNPLAd from '../OfferBNPLAd'
import { getShareModalConfig } from 'lib/customer/getShareModalConfig'
import { isShowPageViewsEnabled, isShowTotalPurchasesEnabled } from 'selectors/socialWidgetSelector'
import { SummaryColumn } from '../styles'
import ShareWithFriendsButton from 'components/Common/ShareWithFriendsButton'
import FlashTourSummary from './FlashTourSummary'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { TourBookmarkButton } from 'tripPlanner/components/Bookmark/BookmarkButton'
import TripGuard from 'tripPlanner/components/TripGuard/TripGuard'
import config from 'constants/config'
import ExpiredDealsLereRecommendations from 'components/Recommendations/ExpiredDeals/ExpiredDealsLereRecommendations'
import ImmersiveSaveButtonPortal from 'tripPlanner/components/ImmersiveHeader/ImmersiveSaveButtonPortal'
import OfferUrgencyLabels from 'components/Common/Labels/OfferUrgencyLabels'
import DepositsAvailableTag from 'components/Common/SecureWithDeposit/DepositsAvailableTag'
import LabelGroup from 'components/Luxkit/Label/LabelGroup'
import CurrencyContext from 'contexts/currencyContext'
import { getDepositAmountPercentage, isDepositAvailableOnOffer } from 'lib/payment/depositsUtils'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import RedirectWithStatus from 'components/Common/RedirectWithStatus'
import offerPageURL from 'lib/offer/offerPageURL'
import { getDefaultDepositAmountPercentage } from 'checkout/selectors/featureConfig/deposit'
import PrimaryButtonPaletteProvider, { PrimaryButtonPalette } from 'contexts/PrimaryButtonPaletteProvider'
import UltraLuxUSPs from '../UltraLux/UltraLuxUSPs'

const Summary = styled(InlineGroup).attrs({
  size: 'lg',
})`
  flex-wrap: wrap;
  word-break: break-word;

  ${mediaQueryUp.tablet} {
    flex-wrap: nowrap;
  }
`

const RelativeBlock = styled.div`
  position: relative;
`

interface Props {
  customerPortalURL: string;
  memberId: string;
  offer: App.TourOffer;
  pathname: string;
  regionCode: string;
  showSocialWidget: boolean;
  isShowPageViewsEnabled: boolean;
  isShowTotalPurchasesEnabled: boolean;
  defaultDepositAmountPercentage: number;
  cruiseLines: Array<App.CruiseLine>;
}

function FlashTourContent(props: Props) {
  const {
    customerPortalURL,
    memberId,
    offer,
    pathname,
    regionCode,
    showSocialWidget,
    isShowPageViewsEnabled,
    isShowTotalPurchasesEnabled,
    defaultDepositAmountPercentage,
    cruiseLines,
  } = props

  const {
    id,
    images,
    locationHeading,
    locationSubheading,
    onlinePurchaseSchedule,
    packages,
  } = offer

  const packageOptionsAnchor = useRef<HTMLDivElement>()

  const scrollToPackageOptions = useCallback(() => {
    packageOptionsAnchor.current.scrollIntoView()
  }, [])

  const availableForPurchase = scheduleIsCurrent(onlinePurchaseSchedule)
  const location = [locationHeading, locationSubheading].filter(t => t).join(', ')

  const shareConfig = getShareModalConfig({
    customerPortalURL,
    image: images[0],
    memberId,
    offerId: id,
    offerLocation: location,
    pathname,
    region: regionCode,
    type: 'offer',
    utmCampaign: 'webshare',
  })

  const allPrices = useMemo(() => offer.packages.map(p => p.price), [offer.packages])
  const currentCurrency = useContext(CurrencyContext)

  const canBookMultipleRooms = config.MULTIPLE_ROOM_BOOKING_ENABLED

  const depositsAvailable = isDepositAvailableOnOffer(offer, currentCurrency, null, offer.lowestPricePackage.price)
  const depositAmountPercentage = getDepositAmountPercentage(offer, defaultDepositAmountPercentage)

  const isUltraLux = offer.productType === 'ultra_lux_tour'

  let paletteValue: PrimaryButtonPalette
  if (isUltraLux) {
    paletteValue = 'ultralux'
  } else {
    paletteValue = 'default'
  }

  if (offer.altLink) {
    return <RedirectWithStatus code={301} to={`/${regionCode.toLowerCase()}${offerPageURL(offer)}`} />
  }

  return (<PrimaryButtonPaletteProvider palette={isUltraLux ? 'ultralux' : 'default'}>
    <ImmersiveSaveButtonPortal>
      <TourBookmarkButton offer={offer} />
    </ImmersiveSaveButtonPortal>
    <ExpiredDealsLereRecommendations
      hideOffers={false}
    />
    <OfferGallery offer={offer} />
    <PrimaryButtonPaletteProvider palette={paletteValue}>
      <RelativeBlock>
        <Pane>
          <PaneBody size="lg-y">
            <LayoutContainer>
              <VerticalSpacer gap={12}>
                <InlineGroup horizontal="between" vertical="center">
                  <LabelGroup>
                    {depositsAvailable && <DepositsAvailableTag offerType={offer.type} depositAmountPercentage={depositAmountPercentage} depositThresholds={offer.depositThresholds} />}
                    <OfferUrgencyLabels offer={offer} showLimitedAvailability />
                  </LabelGroup>
                  <InlineGroup vertical="center" spacing="sm" horizontal="end">
                    <ShareModal shareConfig={shareConfig}>
                      <ShareWithFriendsButton />
                    </ShareModal>
                    <TripGuard>
                      <TourBookmarkButton offer={offer} />
                    </TripGuard>
                  </InlineGroup>
                </InlineGroup>
                <Summary>
                  <OfferIntro
                  offer={offer}
                  isShowPageViewsEnabled={isShowPageViewsEnabled}
                  isShowTotalPurchasesEnabled={isShowTotalPurchasesEnabled}
                  showSocialWidget={showSocialWidget}
                  shareConfig={shareConfig}
                  cruiseLines={cruiseLines}
                />
                  <SummaryColumn>
                    <FlashTourSummary
                      price={offer.lowestPricePackage.price}
                      offer={offer}
                      availableForPurchase={availableForPurchase}
                      showPackageOptions={scrollToPackageOptions}
                    />
                    <OfferBNPLAd prices={allPrices} />
                  </SummaryColumn>
                </Summary>
                {offer.productType === 'ultra_lux_tour' && <UltraLuxUSPs />}
              </VerticalSpacer>
            </LayoutContainer>
          </PaneBody>
        </Pane>
        <ScrollAnchor ref={packageOptionsAnchor} />
        {!canBookMultipleRooms && packages.length > 0 && (
          <PackageOptions
          offer={offer}
          availableForPurchase={availableForPurchase}
          packages={packages}
          defaultDepositAmountPercentage={defaultDepositAmountPercentage}
        />
        )}
        {canBookMultipleRooms && (
          'MULTIPLE ROOM BOOKING IS ENABLED'
        )}
      </RelativeBlock>
    </PrimaryButtonPaletteProvider>
  </PrimaryButtonPaletteProvider>
  )
}

function mapStateToProps(state: App.State) {
  return {
    regionCode: state.geo.currentRegionCode,
    pathname: state.router.location.pathname,
    customerPortalURL: state.config.customerPortalURL,
    memberId: state.auth.account.memberId,
    showSocialWidget: state.ui.socialWidget.display,
    isShowPageViewsEnabled: isShowPageViewsEnabled(state),
    isShowTotalPurchasesEnabled: isShowTotalPurchasesEnabled(state),
    defaultDepositAmountPercentage: getDefaultDepositAmountPercentage(state),
    cruiseLines: state.cruise.cruiseLines,
  }
}

export default connect(mapStateToProps)(FlashTourContent)
